import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Box, Building, Stethoscope, Clock, DollarSign, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const ThreeDModelingService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();

  useEffect(() => {
    const title = {
      de: `3D Modeling & Animation ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `3D Modeling & Animation ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Modélisation 3D & Animation ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Modellazione 3D & Animazione ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Professionelle 3D-Modellierung für Schweizer Unternehmen. Produktvisualisierung, Architektur, Pharma. Höchste Qualität für Uhrmacherei, Medizintechnik & Luxusgüter.`,
      en: `Professional 3D modeling for Swiss businesses. Product visualization, architecture, pharma. Highest quality for watchmaking, medical technology & luxury goods.`,
      fr: `Modélisation 3D professionnelle pour entreprises suisses. Visualisation produits, architecture, pharma. Qualité supérieure pour horlogerie, technologie médicale & luxe.`,
      it: `Modellazione 3D professionale per aziende svizzere. Visualizzazione prodotti, architettura, farmaceutico. Qualità superiore per orologeria, tecnologia medica & lusso.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/3d-modeling${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const services = [
    {
      icon: Box,
      title: {
        de: 'Produktvisualisierung',
        en: 'Product Visualization',
        fr: 'Visualisation Produits',
        it: 'Visualizzazione Prodotti'
      },
      description: {
        de: 'Fotorealistische 3D-Modelle für E-Commerce, Kataloge und Präsentationen',
        en: 'Photorealistic 3D models for e-commerce, catalogs and presentations',
        fr: 'Modèles 3D photoréalistes pour e-commerce, catalogues et présentations',
        it: 'Modelli 3D fotorealistici per e-commerce, cataloghi e presentazioni'
      },
      industries: ['Uhrmacherei', 'Schmuck', 'Luxusgüter', 'Technik'],
      price: 'CHF 800-3,500',
      deliveryTime: '5-10 Tage'
    },
    {
      icon: Building,
      title: {
        de: 'Architektur & Immobilien',
        en: 'Architecture & Real Estate',
        fr: 'Architecture & Immobilier',
        it: 'Architettura & Immobiliare'
      },
      description: {
        de: '3D-Renderings, Grundrisse, virtuelle Rundgänge für Immobilienprojekte',
        en: '3D renderings, floor plans, virtual tours for real estate projects',
        fr: 'Rendus 3D, plans, visites virtuelles pour projets immobiliers',
        it: 'Rendering 3D, planimetrie, tour virtuali per progetti immobiliari'
      },
      industries: ['Immobilien', 'Architektur', 'Bauwesen', 'Innenarchitektur'],
      price: 'CHF 1,500-8,000',
      deliveryTime: '7-14 Tage'
    },
    {
      icon: Stethoscope,
      title: {
        de: 'Medizin & Pharma',
        en: 'Medical & Pharma',
        fr: 'Médical & Pharma',
        it: 'Medico & Farmaceutico'
      },
      description: {
        de: 'Medizinische Illustrationen, Wirkstoff-Animationen, Geräte-Visualisierung',
        en: 'Medical illustrations, drug mechanism animations, device visualization',
        fr: 'Illustrations médicales, animations mécanismes, visualisation appareils',
        it: 'Illustrazioni mediche, animazioni meccanismi, visualizzazione dispositivi'
      },
      industries: ['Pharma', 'Medtech', 'Biotechnologie', 'Forschung'],
      price: 'CHF 2,000-10,000',
      deliveryTime: '10-21 Tage'
    }
  ];

  const swissIndustries = [
    {
      name: {
        de: 'Schweizer Uhrmacherei',
        en: 'Swiss Watchmaking',
        fr: 'Horlogerie Suisse',
        it: 'Orologeria Svizzera'
      },
      description: {
        de: 'Präzise 3D-Modelle von Uhrenkomponenten und kompletten Zeitmessern',
        en: 'Precise 3D models of watch components and complete timepieces',
        fr: 'Modèles 3D précis de composants horlogers et garde-temps complets',
        it: 'Modelli 3D precisi di componenti orologieri e orologi completi'
      },
      examples: ['Rolex', 'Patek Philippe', 'TAG Heuer', 'Vacheron Constantin'],
      avgProject: 'CHF 3,500-15,000'
    },
    {
      name: {
        de: 'Pharmaindustrie',
        en: 'Pharmaceutical Industry',
        fr: 'Industrie Pharmaceutique',
        it: 'Industria Farmaceutica'
      },
      description: {
        de: 'Molekulare Visualisierungen und Wirkstoff-Animationen für Forschung',
        en: 'Molecular visualizations and drug mechanism animations for research',
        fr: 'Visualisations moléculaires et animations mécanismes pour recherche',
        it: 'Visualizzazioni molecolari e animazioni meccanismi per ricerca'
      },
      examples: ['Novartis', 'Roche', 'Lonza', 'Actelion'],
      avgProject: 'CHF 5,000-25,000'
    },
    {
      name: {
        de: 'Luxusimmobilien',
        en: 'Luxury Real Estate',
        fr: 'Immobilier de Luxe',
        it: 'Immobiliare di Lusso'
      },
      description: {
        de: 'Hochwertige Architektur-Renderings für Premium-Immobilienprojekte',
        en: 'High-end architectural renderings for premium real estate projects',
        fr: 'Rendus architecturaux haut de gamme pour projets immobiliers premium',
        it: 'Rendering architettonici di alto livello per progetti immobiliari premium'
      },
      examples: ['Gerzensee', 'Verbier', 'St. Moritz', 'Zermatt'],
      avgProject: 'CHF 4,000-20,000'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: {
        de: 'Schnelle Lieferung',
        en: 'Fast Delivery',
        fr: 'Livraison Rapide',
        it: 'Consegna Veloce'
      },
      description: {
        de: '50% schneller als traditionelle 3D-Studios durch AI-optimierte Workflows',
        en: '50% faster than traditional 3D studios through AI-optimized workflows',
        fr: '50% plus rapide que studios 3D traditionnels via workflows optimisés IA',
        it: '50% più veloce di studi 3D tradizionali tramite flussi ottimizzati IA'
      }
    },
    {
      icon: DollarSign,
      title: {
        de: 'Kosteneffizienz',
        en: 'Cost Efficiency',
        fr: 'Efficacité Coûts',
        it: 'Efficienza Costi'
      },
      description: {
        de: 'Bis zu 60% günstiger als traditionelle 3D-Produktionen bei gleicher Qualität',
        en: 'Up to 60% cheaper than traditional 3D productions with same quality',
        fr: 'Jusqu\'à 60% moins cher que productions 3D traditionnelles, même qualité',
        it: 'Fino al 60% più economico di produzioni 3D tradizionali, stessa qualità'
      }
    },
    {
      icon: Users,
      title: {
        de: 'Swiss Quality Standards',
        en: 'Swiss Quality Standards',
        fr: 'Standards Qualité Suisse',
        it: 'Standard Qualità Svizzera'
      },
      description: {
        de: 'Präzision und Detailgenauigkeit auf höchstem Schweizer Niveau',
        en: 'Precision and attention to detail at the highest Swiss level',
        fr: 'Précision et attention aux détails au plus haut niveau suisse',
        it: 'Precisione e attenzione ai dettagli al più alto livello svizzero'
      }
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Box className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? '3D Modeling & Animation' : 
                 currentLanguage === 'fr' ? 'Modélisation 3D & Animation' :
                 currentLanguage === 'it' ? 'Modellazione 3D & Animazione' : '3D Modeling & Animation'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'Schweizer Präzision in 3D' :
                 currentLanguage === 'fr' ? 'Précision Suisse en 3D' :
                 currentLanguage === 'it' ? 'Precisione Svizzera in 3D' :
                 'Swiss Precision in 3D'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Fotorealistische 3D-Modelle und Animationen für Uhrmacherei, Pharma, Luxusimmobilien und Medizintechnik' :
                 currentLanguage === 'fr' ? 'Modèles 3D et animations photoréalistes pour horlogerie, pharma, immobilier de luxe et technologie médicale' :
                 currentLanguage === 'it' ? 'Modelli 3D e animazioni fotorealistici per orologeria, farmaceutico, immobiliare di lusso e tecnologia medica' :
                 'Photorealistic 3D models and animations for watchmaking, pharma, luxury real estate and medical technology'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'Portfolio ansehen' :
                   currentLanguage === 'fr' ? 'Voir Portfolio' :
                   currentLanguage === 'it' ? 'Vedi Portfolio' :
                   'View Portfolio'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Kostenloses Angebot' :
                   currentLanguage === 'fr' ? 'Devis Gratuit' :
                   currentLanguage === 'it' ? 'Preventivo Gratuito' :
                   'Free Quote'}
                </Button>
              </div>

              {/* Swiss Industry Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">500+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Präzise Modelle' :
                     currentLanguage === 'fr' ? 'Modèles Précis' :
                     currentLanguage === 'it' ? 'Modelli Precisi' :
                     'Precise Models'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">50+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Swiss Brands' :
                     currentLanguage === 'fr' ? 'Marques Suisses' :
                     currentLanguage === 'it' ? 'Brand Svizzeri' :
                     'Swiss Brands'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">5 Tage</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Ø Lieferzeit' :
                     currentLanguage === 'fr' ? 'Délai Moyen' :
                     currentLanguage === 'it' ? 'Tempo Medio' :
                     'Avg. Delivery'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">60%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Kostenersparnis' :
                     currentLanguage === 'fr' ? 'Économies' :
                     currentLanguage === 'it' ? 'Risparmio' :
                     'Cost Savings'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Unsere 3D-Services' :
                 currentLanguage === 'fr' ? 'Nos Services 3D' :
                 currentLanguage === 'it' ? 'I Nostri Servizi 3D' :
                 'Our 3D Services'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="text-center mb-4">
                        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          {service.title[currentLanguage]}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description[currentLanguage]}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                          {service.industries.map((industry, idx) => (
                            <Badge key={idx} variant="secondary" className="justify-center">
                              {industry}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex justify-between items-center mb-4 text-sm">
                          <span className="text-muted-foreground">
                            {currentLanguage === 'de' ? 'Lieferzeit:' :
                             currentLanguage === 'fr' ? 'Délai:' :
                             currentLanguage === 'it' ? 'Consegna:' :
                             'Delivery:'} {service.deliveryTime}
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {service.price}
                          </span>
                        </div>
                        
                        <Button className="w-full">
                          {currentLanguage === 'de' ? 'Mehr erfahren' :
                           currentLanguage === 'fr' ? 'En savoir plus' :
                           currentLanguage === 'it' ? 'Scopri di più' :
                           'Learn More'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Swiss Industries Focus */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Spezialisiert auf Schweizer Schlüsselindustrien' :
                 currentLanguage === 'fr' ? 'Spécialisé dans les Industries Clés Suisses' :
                 currentLanguage === 'it' ? 'Specializzato nelle Industrie Chiave Svizzere' :
                 'Specialized in Swiss Key Industries'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {swissIndustries.map((industry, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">
                      {industry.name[currentLanguage]}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {industry.description[currentLanguage]}
                    </p>
                    <div className="mb-4">
                      <div className="text-sm font-medium mb-2">
                        {currentLanguage === 'de' ? 'Referenzen:' :
                         currentLanguage === 'fr' ? 'Références:' :
                         currentLanguage === 'it' ? 'Riferimenti:' :
                         'References:'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {industry.examples.map((example, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-lg font-bold text-primary">
                      {currentLanguage === 'de' ? 'Ø Projekt:' :
                       currentLanguage === 'fr' ? 'Projet Moyen:' :
                       currentLanguage === 'it' ? 'Progetto Medio:' :
                       'Avg. Project:'} {industry.avgProject}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum Future Level für 3D?' :
                 currentLanguage === 'fr' ? 'Pourquoi Future Level pour la 3D?' :
                 currentLanguage === 'it' ? 'Perché Future Level per il 3D?' :
                 'Why Future Level for 3D?'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {benefit.title[currentLanguage]}
                      </h3>
                      <p className="text-muted-foreground">
                        {benefit.description[currentLanguage]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit für Ihr 3D-Projekt?' :
               currentLanguage === 'fr' ? 'Prêt pour votre projet 3D?' :
               currentLanguage === 'it' ? 'Pronti per il vostro progetto 3D?' :
               'Ready for Your 3D Project?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Lassen Sie uns Ihre Ideen in fotorealistische 3D-Welten verwandeln. Schweizer Qualität, globale Standards.' :
               currentLanguage === 'fr' ? 'Laissez-nous transformer vos idées en mondes 3D photoréalistes. Qualité suisse, standards globaux.' :
               currentLanguage === 'it' ? 'Lasciaci trasformare le vostre idee in mondi 3D fotorealistici. Qualità svizzera, standard globali.' :
               'Let us transform your ideas into photorealistic 3D worlds. Swiss quality, global standards.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Projekt starten' :
                 currentLanguage === 'fr' ? 'Démarrer projet' :
                 currentLanguage === 'it' ? 'Inizia progetto' :
                 'Start Project'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ThreeDModelingService;